<template>
  <div class="dashboard-body-futuristic">
    <main class="futuristic-container" style="flex-direction: column; padding: 1rem 2rem;">
      <section class="dashboard-card-futuristic dashboard-card-scrollable" style="width: 100%;">
        <div class="pdf-viewer-wrapper">

          <Teleport to="#page-action-bar">
            <button class="btn-viewer-action btn-back" @click="goBack">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back
            </button>
            <h1 class="page-bar-title">Form Submission</h1>
            <div class="page-bar-actions">
              <span class="fs-badge" :class="'fs-' + (status || 'none').toLowerCase()">{{ statusLabel }}</span>
              <button v-if="!isViewOnly" class="btn-viewer-action" @click="saveDraft" :disabled="saving || loading">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                {{ saving ? 'Saving…' : 'Save Draft' }}
              </button>
              <button v-if="!isViewOnly" class="btn-viewer-action btn-download" @click="finalSubmit" :disabled="submitting || loading">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {{ submitting ? 'Submitting…' : 'Final Submit' }}
              </button>
              <button v-if="isViewOnly" class="btn-viewer-action btn-download" @click="downloadFinal" :disabled="downloading">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {{ downloading ? 'Downloading…' : 'Download Final PDF' }}
              </button>
            </div>
          </Teleport>

          <!-- Loading -->
          <div v-if="loading" class="fs-state">
            <div class="fs-spinner"></div>
            <span>Extracting forms from the tender… this may take a moment on first open.</span>
          </div>

          <!-- Error -->
          <div v-else-if="loadError" class="fs-state fs-error">
            <p>{{ loadError }}</p>
            <button class="btn-viewer-action" @click="loadSubmission(false)">Retry</button>
          </div>

          <!-- No forms detected -->
          <div v-else-if="!forms.length" class="fs-state">
            <p>No fillable forms were detected in this tender.</p>
            <button class="btn-viewer-action" @click="loadSubmission(true)">Re-extract</button>
          </div>

          <template v-else>
            <!-- Banner: intro + progress -->
            <div class="fs-banner">
              <div class="fs-banner-main">
                <div class="fs-banner-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                <div>
                  <h2 class="fs-banner-title">{{ forms.length }} form{{ forms.length === 1 ? '' : 's' }} extracted from this tender</h2>
                  <p class="fs-banner-sub" v-if="!isViewOnly">Fill in the values, or mark items <em>Not Required</em>, then Final Submit to generate the PDF.</p>
                  <p class="fs-banner-sub" v-else>This submission is finalized and shown in view-only mode.</p>
                </div>
              </div>
              <div class="fs-progress-wrap" v-if="totalFields">
                <div class="fs-progress-label">{{ completedFields }} / {{ totalFields }} fields complete</div>
                <div class="fs-progress-bar"><div class="fs-progress-fill" :style="{ width: progressPct + '%' }"></div></div>
              </div>
            </div>

            <!-- Validation summary -->
            <div v-if="validationErrors.length" class="fs-validation">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span><strong>{{ validationErrors.length }} required field(s) are incomplete.</strong>
                Fill them below (highlighted), or tick <em>N/R</em>, then submit again.</span>
            </div>

            <div class="fs-layout">
              <!-- Sticky forms index -->
              <aside class="fs-nav">
                <div class="fs-nav-title">Forms</div>
                <button v-for="form in forms" :key="'nav' + form.index"
                        class="fs-nav-item" :class="{ 'fs-nav-done': isFormComplete(form) }"
                        @click="scrollToForm(form.index)">
                  <span class="fs-nav-dot"></span>
                  <span class="fs-nav-name">{{ form.form_name }}</span>
                  <span class="fs-nav-count">{{ formFieldStats(form).done }}/{{ formFieldStats(form).total }}</span>
                </button>
              </aside>

              <!-- Forms -->
              <div class="fs-forms">
                <div v-for="form in forms" :key="form.index" class="fs-form-card" :ref="'form' + form.index"
                     :class="{ 'fs-form-nr': notRequired['form_' + form.index] }">
                  <div class="fs-form-head">
                    <div class="fs-form-head-left">
                      <span class="fs-form-num">{{ form.index + 1 }}</span>
                      <div>
                        <h3>{{ form.form_name }}</h3>
                        <p v-if="form.description" class="fs-form-desc">{{ form.description }}</p>
                      </div>
                    </div>
                    <label class="fs-nr-toggle">
                      <input type="checkbox" :disabled="isViewOnly"
                             :checked="notRequired['form_' + form.index]"
                             @change="toggleFormNotRequired(form.index, $event.target.checked)" />
                      Not Required
                    </label>
                  </div>

                  <div v-if="notRequired['form_' + form.index]" class="fs-nr-note">
                    Marked Not Required — this form will be skipped in the final PDF.
                  </div>

                  <div v-else class="fs-form-body">
                    <template v-for="(block, bIdx) in form.blocks" :key="bIdx">
                      <!-- Heading -->
                      <h4 v-if="block.kind === 'heading'" class="fs-heading">{{ block.text }}</h4>

                      <!-- Instruction -->
                      <p v-else-if="block.kind === 'instruction'" class="fs-instruction">{{ block.text }}</p>

                      <!-- Field -->
                      <div v-else-if="block.kind === 'field'" class="fs-field"
                           :class="{ 'fs-field-invalid': isInvalid(block.key), 'fs-field-nr': notRequired[block.key] }">
                        <label class="fs-field-label">
                          <span class="fs-field-label-text">{{ block.label }}</span>
                          <span v-if="block.value_type && block.value_type !== 'text'" class="fs-type">{{ block.value_type }}</span>
                        </label>
                        <div class="fs-field-control">
                          <!-- boolean -->
                          <select v-if="block.value_type === 'boolean'" v-model="draftData[block.key]"
                                  :disabled="isViewOnly || notRequired[block.key]" class="fs-input">
                            <option value="">— Select —</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                          <!-- multiline -->
                          <textarea v-else-if="block.value_type === 'multiline'" v-model="draftData[block.key]"
                                    :disabled="isViewOnly || notRequired[block.key]" rows="2" class="fs-input"
                                    placeholder="Enter value"></textarea>
                          <!-- date -->
                          <input v-else-if="block.value_type === 'date'" type="date" v-model="draftData[block.key]"
                                 :disabled="isViewOnly || notRequired[block.key]" class="fs-input" />
                          <!-- number / currency -->
                          <input v-else-if="block.value_type === 'number' || block.value_type === 'currency'"
                                 type="text" inputmode="decimal" v-model="draftData[block.key]"
                                 :disabled="isViewOnly || notRequired[block.key]" class="fs-input"
                                 :placeholder="block.value_type === 'currency' ? 'Amount' : 'Number'" />
                          <!-- text -->
                          <input v-else type="text" v-model="draftData[block.key]"
                                 :disabled="isViewOnly || notRequired[block.key]" class="fs-input"
                                 placeholder="Enter value" />

                          <label class="fs-nr-inline" :title="'Mark ' + block.label + ' as Not Required'">
                            <input type="checkbox" :disabled="isViewOnly"
                                   :checked="notRequired[block.key]"
                                   @change="toggleFieldNotRequired(block.key, $event.target.checked)" />
                            N/R
                          </label>
                        </div>
                      </div>

                      <!-- Table -->
                      <div v-else-if="block.kind === 'table'" class="fs-table-block">
                        <div class="fs-table-head-row">
                          <span class="fs-table-title">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="3" x2="9" y2="21" />
                            </svg>
                            {{ block.label || 'Details' }}
                            <span class="fs-table-hint">— {{ block.rows_hint || 'add a row per entry' }}</span>
                          </span>
                          <button v-if="!isViewOnly" class="fs-row-btn" @click="addRow(block)">+ Add Row</button>
                        </div>
                        <div class="table-responsive">
                          <table class="pdf-content-table fs-table">
                            <thead>
                              <tr>
                                <th v-for="(col, ci) in block.columns" :key="ci">{{ col }}</th>
                                <th v-if="!isViewOnly" style="width:52px;"></th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(row, ri) in tableRows(block)" :key="ri">
                                <td v-for="(col, ci) in block.columns" :key="ci">
                                  <input type="text" v-model="row[col]" :disabled="isViewOnly" class="fs-input fs-cell" />
                                </td>
                                <td v-if="!isViewOnly">
                                  <button class="fs-row-del" @click="removeRow(block, ri)" title="Remove row" aria-label="Remove row">×</button>
                                </td>
                              </tr>
                              <tr v-if="!tableRows(block).length">
                                <td :colspan="(block.columns ? block.columns.length : 1) + (isViewOnly ? 0 : 1)"
                                    class="fs-table-empty">
                                  No rows yet. <button v-if="!isViewOnly" class="fs-link" @click="addRow(block)">Add the first row</button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </template>

                    <div v-if="!formHasInputs(form)" class="fs-no-fields">
                      This form has no auto-detected fillable fields. Review the tender form directly if entries are required.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

        </div>
      </section>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import { notify } from '@/utils/notify';
import { confirmAction } from '@/utils/confirm';

export default {
  name: "FormSubmission",
  props: { getAccessToken: Function },

  data() {
    return {
      loading: true,
      loadError: '',
      saving: false,
      submitting: false,
      downloading: false,
      status: 'DRAFT',
      forms: [],
      draftData: {},
      notRequired: {},
      validationErrors: [],
    };
  },

  computed: {
    pdfId() { return this.$route.params.pdf_id; },
    isViewOnly() { return this.status === 'SUBMITTED'; },
    statusLabel() {
      const map = { DRAFT: 'Draft', SUBMITTED: 'Submitted (View Only)', NONE: 'New' };
      return map[this.status] || this.status;
    },
    invalidKeys() {
      return new Set(this.validationErrors.map(e => e.key));
    },
    // Scalar fields across all forms (tables excluded from the counter).
    allScalarFields() {
      const out = [];
      for (const form of this.forms) {
        if (this.notRequired['form_' + form.index]) continue;
        for (const b of (form.blocks || [])) {
          if (b.kind === 'field') out.push(b);
        }
      }
      return out;
    },
    totalFields() { return this.allScalarFields.length; },
    completedFields() {
      return this.allScalarFields.filter(f => this.fieldDone(f.key)).length;
    },
    progressPct() {
      return this.totalFields ? Math.round((this.completedFields / this.totalFields) * 100) : 0;
    },
  },

  mounted() {
    this.loadSubmission(false);
  },

  methods: {
    authHeaders() { return { Authorization: 'Bearer ' + this.getAccessToken() }; },
    goBack() {
      // Back to the bid's Analysis tab; carry status/from so the sidebar keeps its highlight.
      const { status, from } = this.$route.query;
      this.$router.push({
        name: 'BidDetails',
        params: { pdf_id: this.$route.params.pdf_id },
        query: { tab: 'analysis', ...(status ? { status } : {}), ...(from ? { from } : {}) },
      });
    },

    isInvalid(key) { return this.invalidKeys.has(key); },

    // A field counts as "done" when it has a value OR is marked Not Required.
    fieldDone(key) {
      if (this.notRequired[key]) return true;
      const v = this.draftData[key];
      return v !== undefined && v !== null && String(v).trim() !== '';
    },
    formFieldStats(form) {
      const fields = (form.blocks || []).filter(b => b.kind === 'field');
      const done = fields.filter(f => this.fieldDone(f.key)).length;
      return { total: fields.length, done };
    },
    isFormComplete(form) {
      if (this.notRequired['form_' + form.index]) return true;
      const s = this.formFieldStats(form);
      return s.total === 0 ? false : s.done === s.total;
    },
    formHasInputs(form) {
      return (form.blocks || []).some(b => b.kind === 'field' || b.kind === 'table');
    },
    scrollToForm(index) {
      const el = this.$refs['form' + index];
      const node = Array.isArray(el) ? el[0] : el;
      if (node && node.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    // Stable storage key for a table block. The backend assigns block.key; keep a
    // deterministic fallback for older drafts that predate table keys.
    tableKey(block) {
      return block.key || ('tbl_' + (block.columns || []).join('_').replace(/[^a-z0-9]+/gi, '_').toLowerCase());
    },
    // Ensure every table has a reactive array in draftData BEFORE render, so
    // v-model on each cell works and rows persist. Called once after load.
    initTables() {
      for (const form of this.forms) {
        for (const block of (form.blocks || [])) {
          if (block.kind !== 'table') continue;
          const key = this.tableKey(block);
          const existing = this.draftData[key];
          // Normalize any previously-saved rows to plain objects keyed by column.
          if (Array.isArray(existing)) {
            this.draftData[key] = existing.map(r => this.normalizeRow(block, r));
          } else {
            // Start every table with one empty row so the inputs are visible.
            this.draftData[key] = [this.blankRow(block)];
          }
        }
      }
    },
    blankRow(block) {
      const row = {};
      (block.columns || []).forEach(c => { row[c] = ''; });
      return row;
    },
    normalizeRow(block, r) {
      const row = this.blankRow(block);
      if (r && typeof r === 'object' && !Array.isArray(r)) {
        (block.columns || []).forEach(c => { if (r[c] !== undefined) row[c] = r[c]; });
      } else if (Array.isArray(r)) {
        (block.columns || []).forEach((c, i) => { row[c] = r[i] !== undefined ? r[i] : ''; });
      }
      return row;
    },
    tableRows(block) {
      const key = this.tableKey(block);
      if (!Array.isArray(this.draftData[key])) this.draftData[key] = [];
      return this.draftData[key];
    },
    addRow(block) {
      const key = this.tableKey(block);
      if (!Array.isArray(this.draftData[key])) this.draftData[key] = [];
      this.draftData[key].push(this.blankRow(block));
    },
    removeRow(block, ri) {
      const key = this.tableKey(block);
      if (Array.isArray(this.draftData[key])) this.draftData[key].splice(ri, 1);
    },

    toggleFieldNotRequired(key, checked) {
      if (checked) {
        this.notRequired[key] = true;
        this.draftData[key] = '';
      } else {
        delete this.notRequired[key];
      }
    },
    toggleFormNotRequired(index, checked) {
      const k = 'form_' + index;
      if (checked) this.notRequired[k] = true;
      else delete this.notRequired[k];
    },

    async loadSubmission(refresh) {
      this.loading = true;
      this.loadError = '';
      try {
        const res = await axios.get(
          `${this.$API_URL}/form-fill/${this.pdfId}/submission${refresh ? '?refresh=true' : ''}`,
          { headers: this.authHeaders() }
        );
        const d = res.data || {};
        this.status = d.status || 'DRAFT';
        this.forms = Array.isArray(d.forms) ? d.forms : [];
        this.draftData = d.draft_data || {};
        this.notRequired = d.not_required || {};
        this.validationErrors = [];
        // Seed reactive table arrays (one empty row each) before render so the
        // row-wise inputs (e.g. Name / Mobile / Email) are visible immediately.
        this.initTables();
      } catch (err) {
        console.error('Failed to load form submission', err);
        this.loadError = (err.response && err.response.data && err.response.data.detail)
          || 'Failed to load the bid forms. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async saveDraft() {
      this.saving = true;
      try {
        const res = await axios.post(`${this.$API_URL}/form-fill/submission/draft`, {
          pdf_id: Number(this.pdfId),
          draft_data: this.draftData,
          not_required: this.notRequired,
        }, { headers: this.authHeaders() });
        this.status = (res.data && res.data.status) || this.status;
        notify.success('Draft saved');
      } catch (err) {
        console.error('Save draft failed', err);
        const detail = err.response && err.response.data && err.response.data.detail;
        notify.error(typeof detail === 'string' ? detail : 'Couldn’t save the draft. Check your connection and try again.');
      } finally {
        this.saving = false;
      }
    },

    async finalSubmit() {
      const ok = await confirmAction({
        title: 'Submit Forms?',
        message: 'The final PDF is generated and the forms become view-only. You can’t edit them after submitting.',
        confirmLabel: 'Submit Forms',
      });
      if (!ok) return;
      this.submitting = true;
      this.validationErrors = [];
      try {
        const res = await axios.post(`${this.$API_URL}/form-fill/submission/final`, {
          pdf_id: Number(this.pdfId),
          draft_data: this.draftData,
          not_required: this.notRequired,
        }, { headers: this.authHeaders() });
        this.status = (res.data && res.data.status) || 'SUBMITTED';
        notify.success('Forms submitted. The final PDF is ready to download.');
      } catch (err) {
        const data = err.response && err.response.data;
        // 422 => structured validation failure with a missing-fields list.
        if (err.response && err.response.status === 422 && data && data.detail && Array.isArray(data.detail.missing)) {
          this.validationErrors = data.detail.missing;
          // Scroll to top so the summary banner is visible.
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          console.error('Final submit failed', err);
          const msg = data && (data.detail || data.message);
          notify.error(typeof msg === 'string' ? msg : 'Couldn’t submit the forms. Check your connection and try again.');
        }
      } finally {
        this.submitting = false;
      }
    },

    async downloadFinal() {
      this.downloading = true;
      try {
        const res = await axios.get(`${this.$API_URL}/form-fill/${this.pdfId}/submission/final-pdf`, {
          responseType: 'blob',
          headers: this.authHeaders(),
        });
        let filename = `filled_forms_${this.pdfId}.pdf`;
        const disp = res.headers['content-disposition'] || res.headers['Content-Disposition'];
        if (disp) {
          const m = disp.match(/filename\*?=(?:UTF-8''|")?([^;"\n]+)/i);
          if (m && m[1]) filename = decodeURIComponent(m[1].replace(/['"]/g, ''));
        }
        const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } catch (err) {
        console.error('Download failed', err);
        notify.error('Couldn’t download the final PDF. Check your connection and try again.');
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ===== Header badge ===== */
.fs-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  background: #e2e8f0;
  color: #475569;
}
.fs-badge.fs-draft { background: #fef3c7; color: #b45309; }
.fs-badge.fs-submitted { background: #dcfce7; color: #15803d; }

/* ===== Loading / error / empty states ===== */
.fs-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3.5rem 1rem;
  color: #64748b;
  text-align: center;
}
.fs-state.fs-error { color: #b91c1c; }
.fs-spinner {
  width: 38px; height: 38px;
  border: 4px solid #ede9fe;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: fsspin 0.9s linear infinite;
}
@keyframes fsspin { to { transform: rotate(360deg); } }

/* ===== Banner + progress ===== */
.fs-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  background: linear-gradient(120deg, #f5f3ff 0%, #eef2ff 100%);
  border: 1px solid #e9d5ff;
  border-radius: 16px;
  padding: 1.1rem 1.4rem;
  margin-bottom: 1.1rem;
}
.fs-banner-main { display: flex; align-items: center; gap: 1rem; min-width: 0; }
.fs-banner-icon {
  flex-shrink: 0;
  width: 46px; height: 46px;
  border-radius: 12px;
  background: #7c3aed;
  color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  box-shadow: 0 8px 18px rgba(124, 58, 237, 0.25);
}
.fs-banner-title { margin: 0; font-size: 1.05rem; color: #1e1b4b; font-weight: 800; }
.fs-banner-sub { margin: 0.2rem 0 0; font-size: 0.85rem; color: #6d5ba6; }
.fs-banner-sub em { font-style: normal; font-weight: 700; }
.fs-progress-wrap { min-width: 220px; flex: 0 0 auto; }
.fs-progress-label { font-size: 0.78rem; font-weight: 700; color: #6d5ba6; margin-bottom: 0.35rem; text-align: right; }
.fs-progress-bar { height: 9px; background: #e9d5ff; border-radius: 999px; overflow: hidden; }
.fs-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #8b5cf6, #6d28d9);
  border-radius: 999px;
  transition: width 0.35s ease;
}

/* ===== Validation banner ===== */
.fs-validation {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  margin-bottom: 1.1rem;
  font-size: 0.9rem;
}
.fs-validation svg { flex-shrink: 0; margin-top: 1px; }
.fs-validation em { font-style: normal; font-weight: 700; }

/* ===== Two-column layout ===== */
.fs-layout { display: grid; grid-template-columns: 240px minmax(0, 1fr); gap: 1.25rem; align-items: start; }
.fs-forms { min-width: 0; }

/* ===== Sticky nav ===== */
.fs-nav {
  position: sticky;
  top: 0.5rem;
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 0.75rem;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}
.fs-nav-title {
  font-size: 0.72rem; font-weight: 800; letter-spacing: 0.06em;
  text-transform: uppercase; color: #64748b; padding: 0.2rem 0.5rem 0.5rem;
}
.fs-nav-item {
  display: flex; align-items: center; gap: 0.5rem;
  width: 100%;
  background: none; border: none; text-align: left;
  padding: 0.5rem 0.55rem;
  border-radius: 8px;
  cursor: pointer;
  color: #475569;
  font-size: 0.82rem;
  transition: background 0.15s;
}
.fs-nav-item:hover { background: #f5f3ff; }
.fs-nav-dot { width: 8px; height: 8px; border-radius: 50%; background: #cbd5e1; flex-shrink: 0; }
.fs-nav-done .fs-nav-dot { background: #16a34a; }
.fs-nav-name { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
.fs-nav-count { font-size: 0.72rem; color: #64748b; font-weight: 700; flex-shrink: 0; }

/* ===== Form card ===== */
.fs-form-card {
  border: 1px solid #e8ecf3;
  border-radius: 16px;
  padding: 1.15rem 1.35rem 1.3rem;
  margin-bottom: 1.25rem;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05);
  scroll-margin-top: 0.75rem;
}
.fs-form-card.fs-form-nr { opacity: 0.72; }
.fs-form-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
  border-bottom: 1px solid #eef2f7;
  padding-bottom: 0.75rem;
  margin-bottom: 0.9rem;
}
.fs-form-head-left { display: flex; align-items: flex-start; gap: 0.75rem; min-width: 0; }
.fs-form-num {
  flex-shrink: 0;
  width: 28px; height: 28px;
  border-radius: 8px;
  background: #ede9fe; color: #6d28d9;
  font-weight: 800; font-size: 0.85rem;
  display: inline-flex; align-items: center; justify-content: center;
}
.fs-form-head h3 { margin: 0; font-size: 1.02rem; color: #0f172a; line-height: 1.3; }
.fs-form-desc { color: #64748b; font-size: 0.82rem; margin: 0.2rem 0 0; }

.fs-nr-toggle, .fs-nr-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.76rem;
  color: #64748b;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
}
.fs-nr-toggle input, .fs-nr-inline input { accent-color: #7c3aed; cursor: pointer; }
.fs-nr-note {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  color: #64748b;
  font-size: 0.85rem;
}
.fs-no-fields {
  background: #fffbeb;
  border: 1px dashed #fcd34d;
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  color: #92700e;
  font-size: 0.83rem;
}

/* ===== Blocks ===== */
.fs-form-body { display: block; }
.fs-heading { margin: 1rem 0 0.5rem; font-size: 0.92rem; color: #334155; font-weight: 800; }
.fs-instruction { color: #64748b; font-size: 0.83rem; margin: 0.35rem 0; line-height: 1.5; }

.fs-field { margin: 0.65rem 0; padding: 0.1rem 0; }
.fs-field-label {
  display: flex; align-items: center; gap: 0.5rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #334155;
  margin-bottom: 0.3rem;
}
.fs-type {
  font-size: 0.66rem; font-weight: 700; letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #7c3aed; background: #f3e8ff;
  padding: 0.05rem 0.4rem; border-radius: 6px;
}
.fs-field-control { display: flex; align-items: center; gap: 0.6rem; }
.fs-input {
  flex: 1 1 auto;
  border: 1px solid #d5dce6;
  border-radius: 8px;
  padding: 0.55rem 0.75rem;
  font-size: 0.9rem;
  background: #fff;
  color: #1e293b;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.fs-input:focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12); }
.fs-input:disabled { background: #f1f5f9; color: #64748b; cursor: not-allowed; }
textarea.fs-input { resize: vertical; min-height: 40px; }
.fs-field-invalid .fs-input { border-color: #ef4444; background: #fff5f5; }
.fs-field-invalid .fs-field-label { color: #b91c1c; }
.fs-field-nr .fs-input { background: #f8fafc; }
.fs-nr-inline {
  flex-shrink: 0;
  padding: 0.3rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

/* ===== Tables ===== */
.fs-cell { padding: 0.4rem 0.55rem; font-size: 0.85rem; border-radius: 6px; }
.fs-table-block {
  margin: 0.9rem 0;
  background: #fbfcfe;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 0.8rem 0.9rem;
}
.fs-table-head-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.55rem;
}
.fs-table-title { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; font-weight: 800; color: #334155; flex-wrap: wrap; }
.fs-table-title svg { color: #7c3aed; }
.fs-table-hint { font-weight: 500; color: #64748b; font-size: 0.76rem; }
.fs-row-btn {
  background: #7c3aed;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  font-weight: 700;
  font-size: 0.78rem;
  cursor: pointer;
  transition: background 0.15s;
}
.fs-row-btn:hover { background: #6d28d9; }
.fs-row-del {
  background: #fee2e2;
  color: #b91c1c;
  border: none;
  border-radius: 6px;
  width: 26px; height: 26px;
  font-size: 1.15rem;
  line-height: 1;
  cursor: pointer;
}
.fs-row-del:hover { background: #fecaca; }
.fs-table :deep(thead th) { background: #e0f2fe; color: #0c4a6e; font-size: 0.8rem; }
.fs-table :deep(td) { padding: 0.3rem 0.4rem; }
.fs-table-empty { text-align: center; color: #64748b; padding: 1rem; font-size: 0.85rem; }
.fs-link { background: none; border: none; color: #7c3aed; font-weight: 700; cursor: pointer; text-decoration: underline; }

@media (max-width: 900px) {
  .fs-layout { grid-template-columns: 1fr; }
  .fs-nav { position: static; max-height: none; display: flex; flex-wrap: wrap; gap: 0.35rem; }
  .fs-nav-title { width: 100%; }
  .fs-nav-item { width: auto; border: 1px solid #eef2f7; }
  .fs-banner { flex-direction: column; align-items: stretch; }
  .fs-progress-wrap { min-width: 0; }
  .fs-progress-label { text-align: left; }
}
</style>

